var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { getCookie } from './getCookie';
import { AppContextEnum, BankEnum, BusinessUnitEnum, CookieEnum, DestinationEnum } from './types/enums';
import optinChecker, { OPTIN_TYPES } from '@sparebank1/optin-checker';
import { extractBankFromUri, extractBankSubBrandFromUri } from './bankUtil';
import { DEFAULT_DESTINATIONS, PREVENTION_COOKIE_VALUE } from './types/constants';
/**
 * Util function which can be used to provide sane defaults for digitalbank teams
 * Refer {@link InitParams} for details of usage in tracker
 *
 * Example:
 * ```typescript
 * const myTracker = initWebBehaviorTracker(() => withDigitalBankDefaults({
 *   application: {
 *     name: "kundefront-pm-sparing",
 *     version: "1.3.0",
 *   },
 *   language: LanguageEnum.nb,
 *   adobeEnv: AdobeEnvEnum.Dev
 * }));
 * ```
 *
 * @param options
 *
 * @category Helper functions
 */
export function withDigitalBankDefaults(options) {
    var _a;
    var _b, _c, _d, _e, _f;
    return {
        user: {
            anonymousId: (_b = getCookie(CookieEnum.DeclaredId)) !== null && _b !== void 0 ? _b : '',
            sessionId: (_c = getCookie(CookieEnum.SessionId)) !== null && _c !== void 0 ? _c : '',
            optins: {
                functional: optinChecker.hasOptedIn(OPTIN_TYPES.FUNKSJONELL),
                statistics: optinChecker.hasOptedIn(OPTIN_TYPES.STATISTIKK),
                marketing: optinChecker.hasOptedIn(OPTIN_TYPES.MARKEDSFORING),
            },
            language: options.language,
        },
        adobe: {
            businessUnit: BusinessUnitEnum.SpareBank1,
            adobeEnv: options.adobeEnv,
            identityCookiesEnabled: true,
            identityCookies: (_a = {},
                _a[CookieEnum.AdobeIdentity] = (_d = getCookie(CookieEnum.AdobeIdentity)) !== null && _d !== void 0 ? _d : '',
                _a[CookieEnum.AdobeAmcv] = (_e = getCookie(CookieEnum.AdobeAmcv)) !== null && _e !== void 0 ? _e : '',
                _a),
            subBrand: extractBankSubBrandFromUri(),
        },
        product: options.product,
        destinations: DEFAULT_DESTINATIONS,
        bank: extractBankFromUri(),
        application: __assign(__assign({}, options.application), { context: AppContextEnum.digitalbank }),
        customData: options.customData,
        disableTrackingCookies: (_f = options.disableTrackingCookies) !== null && _f !== void 0 ? _f : false,
    };
}
/**
 * Util function which can be used to provide sane defaults for tracking in internal apps
 * Refer {@link InitParams} for details of usage in tracker
 * @param options
 *
 * ```typescript
 * const myTracker = initWebBehaviorTracker(() => withInternalDefaults({
 *   application: {
 *     name: "kontorfront-raadgiverlosning",
 *     version: "1.3.0",
 *   },
 *   language: LanguageEnum.nb,
 *   // Optional value. Only required if the application is running outside the test.sparebank1.no or sparebank1.no domain (including subdomain)
 *   backendEnvironment: BackendEnvironmentEnum.Test
 * }));
 * ```
 *
 * @category Helper functions
 */
export function withInternalDefaults(options) {
    var _a;
    return {
        user: {
            anonymousId: PREVENTION_COOKIE_VALUE,
            sessionId: (_a = getCookie(CookieEnum.SessionId)) !== null && _a !== void 0 ? _a : '',
            optins: {
                functional: optinChecker.hasOptedIn(OPTIN_TYPES.FUNKSJONELL),
                statistics: optinChecker.hasOptedIn(OPTIN_TYPES.STATISTIKK),
                marketing: optinChecker.hasOptedIn(OPTIN_TYPES.MARKEDSFORING),
            },
            language: options.language,
        },
        adobe: undefined,
        product: options.product,
        destinations: [DestinationEnum.Humio],
        bank: BankEnum.None,
        application: __assign(__assign({}, options.application), { context: AppContextEnum.intern }),
        customData: options.customData,
        disableTrackingCookies: true,
        backendEnvironment: options.backendEnvironment,
    };
}
//# sourceMappingURL=defaults.js.map